import React from 'react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import authService, { type Store as StoreType } from '../../../services/authService';
import type { StoreDetailsTab } from '../../../types/features/storeDetails/page';
import type { StoreFormData } from '../../../types/features/storeDetails/storeForm';
import { isValidStoreDetailsTab } from '../utils/storeDetailsPageUtils';
import { normalizeStoreUpdatePayload } from '../storeEdit/utils/storeDetailsStoreFormUtils';
import { STORE_DETAILS_TAB_PARAM, STORE_DETAILS_TAB_PARAM_LEGACY } from '../utils/storeDetailsQueryParams';

export const useStoreDetailsPage = () => {
    const { storeId } = useParams<{ storeId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [store, setStore] = React.useState<StoreType | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [activeTab, setActiveTab] = React.useState<StoreDetailsTab>('overview');
    const [editDialogOpen, setEditDialogOpen] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const userDetails = React.useMemo(() => JSON.parse(localStorage.getItem('userDetails') || '{}'), []);
    const isSuperAdmin = userDetails?.is_super_admin || false;
    const todayDate = dayjs().format('YYYY-MM-DD');

    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tabFromUrl = params.get(STORE_DETAILS_TAB_PARAM);
        const legacyTabFromUrl = params.get(STORE_DETAILS_TAB_PARAM_LEGACY);
        const candidateTab = tabFromUrl || legacyTabFromUrl;

        if (legacyTabFromUrl && !tabFromUrl) {
            const desired = candidateTab === 'overview' ? null : candidateTab;
            if (desired && isValidStoreDetailsTab(desired)) {
                params.set(STORE_DETAILS_TAB_PARAM, desired);
            } else {
                params.delete(STORE_DETAILS_TAB_PARAM);
            }
            params.delete(STORE_DETAILS_TAB_PARAM_LEGACY);

            const nextSearch = params.toString();
            navigate(
                {
                    pathname: location.pathname,
                    search: nextSearch ? `?${nextSearch}` : '',
                },
                { replace: true },
            );
            return;
        }

        if (!isValidStoreDetailsTab(candidateTab)) {
            return;
        }

        setActiveTab((previous) => (previous === candidateTab ? previous : candidateTab));
    }, [location.pathname, location.search, navigate]);

    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tabFromUrl = params.get(STORE_DETAILS_TAB_PARAM);
        const desiredTabParam = activeTab === 'overview' ? null : activeTab;

        if (desiredTabParam === tabFromUrl) {
            return;
        }

        if (desiredTabParam) {
            params.set(STORE_DETAILS_TAB_PARAM, desiredTabParam);
        } else {
            params.delete(STORE_DETAILS_TAB_PARAM);
        }

        params.delete(STORE_DETAILS_TAB_PARAM_LEGACY);

        const nextSearch = params.toString();
        const currentSearch = location.search.startsWith('?') ? location.search.slice(1) : location.search;
        if (nextSearch !== currentSearch) {
            navigate(
                {
                    pathname: location.pathname,
                    search: nextSearch ? `?${nextSearch}` : '',
                },
                { replace: true },
            );
        }
    }, [activeTab, location.pathname, location.search, navigate]);

    const loadStore = React.useCallback(async () => {
        if (!storeId) return;
        try {
            setLoading(true);
            setError('');
            const data = await authService.getStoreDetails(storeId);
            setStore(data);
        } catch (err) {
            console.error('Error loading store details:', err);
            setError('Unable to load store details. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [storeId]);

    const loadStoreSilently = React.useCallback(async (): Promise<StoreType | null> => {
        if (!storeId) return null;
        try {
            const data = await authService.getStoreDetails(storeId);
            setStore(data);
            return data;
        } catch (err) {
            console.error('Error silently refreshing store details:', err);
            return null;
        }
    }, [storeId]);

    React.useEffect(() => {
        if (!isSuperAdmin) {
            setLoading(false);
            setError('Access denied. Super admin privileges required.');
            return;
        }
        void loadStore();
    }, [isSuperAdmin, loadStore]);

    const handleSaveStore = React.useCallback(async (nextStoreId: string, data: StoreFormData) => {
        try {
            setSaving(true);
            const updated = await authService.updateStore(nextStoreId, normalizeStoreUpdatePayload(data));
            setStore(updated);
            toast.success('Store updated successfully');
            setEditDialogOpen(false);
        } catch (err) {
            console.error('Error updating store:', err);
            toast.error('Failed to update store. Please try again.');
            throw err;
        } finally {
            setSaving(false);
        }
    }, []);

    const handleBack = React.useCallback(() => {
        const params = new URLSearchParams(location.search);
        params.delete(STORE_DETAILS_TAB_PARAM);
        params.delete(STORE_DETAILS_TAB_PARAM_LEGACY);

        const search = params.toString();
        navigate(`/dashboard/store-summary${search ? `?${search}` : ''}`);
    }, [location.search, navigate]);

    return {
        activeTab,
        editDialogOpen,
        error,
        handleBack,
        handleCloseEditDialog: () => setEditDialogOpen(false),
        handleOpenEditDialog: () => setEditDialogOpen(true),
        handleSaveStore,
        isSuperAdmin,
        loadStore,
        loadStoreSilently,
        loading,
        navigate,
        saving,
        setActiveTab,
        store,
        storeId,
        todayDate,
    };
};
